/**
 * Sesotho: Settings
 *
 * What this file covers: Settings: profile, organisation, business hours, team and roles, notifications,
 * security, appearance and the other settings pages.
 *
 * Tone: Neutral and precise. Each setting says what changes when it is switched on.
 *
 * Sesotho: Everyday Sesotho as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const settings: Catalog = {
  'settings.nav.developer': 'Moqapi',
  'settings.apiKeys.actions.create': 'Theha senotlolo sa API',
  'settings.apiKeys.columns.prefix': 'Sehlomathiso',
  'settings.apiKeys.empty': 'Ha ho linotlolo tsa API hajoale.',
  'settings.apiKeys.form.name': 'Lebitso la senotlolo',
  'settings.apiKeys.loadError': 'Ha ea khona ho kenya linotlolo tsa API.',
  'settings.apiKeys.modal.revoke.title': 'Hlakola senotlolo sa API?',
  'settings.apiKeys.toast.revoked': 'Senotlolo sa API se hlakotsoe.',
  'settings.appearance.comfortable': 'Ho phutholohile',
  'settings.appearance.compact': 'Ho tiile',
  'settings.appearance.cozy': 'Ho futhumetse',
  'settings.appearance.density': 'Botenya',
  'settings.appearance.theme': 'Morero',
  'settings.badge.emailUnverified': 'Imeile ha e netefatsoa',
  'settings.badge.twoFactorOff': '2FA e timme',
  'settings.badge.twoFactorOn': '2FA e bulehile',
  'settings.cannedReplies.deleteFailed': 'Ha ea khona ho hlakola.',
  'settings.cannedReplies.loadError': 'Ha ea khona ho kenya likarabo tse lokiselitsoeng.',
  'settings.nav.apiKeys': 'Linotlolo tsa API',
  'settings.nav.appearance': 'Ponahalo',
  'settings.nav.notifications': 'Litsebiso',
  'settings.nav.organisation': 'Mokhatlo',
  'settings.nav.security': 'Ts\'ireletso',
  'settings.nav.team': 'Sehlopha',
  'settings.nav.webhooks': 'Li-webhook',
  'settings.notifications.actions.save': 'Boloka likhetho',
  'settings.notifications.title': 'Litsebiso tsa imeile',
  'settings.notifications.toast.saved': 'Likhetho tsa litsebiso li bolokiloe.',
  'settings.organisation.timezone': 'Sebaka sa nako',
  'settings.organisation.toast.saved': 'Mokhatlo o nchafalitsoe.',
  'settings.profile.toast.saved': 'Boitsebiso bo nchafalitsoe.',
  'settings.security.actions.signOutAll': 'Tsoa hohle',
  'settings.security.activeSessions': 'Liseshene tse sebetsang',
  'settings.security.changePassword': 'Fetola phasewete',
  'settings.security.confirmNewPassword': 'Netefatsa phasewete e ncha',
  'settings.security.currentPassword': 'Phasewete ea hajoale',
  'settings.security.modal.signOutAll.title': 'Tsoa hohle?',
  'settings.security.toast.changed': 'Phasewete e fetotsoe.',
  'settings.security.twoFactor.actions.disable': 'Thibela 2FA',
  'settings.security.twoFactor.actions.enable': 'Lumella 2FA',
  'settings.security.twoFactor.actions.setup': 'Hlophisa 2FA',
  'settings.security.twoFactor.code': 'Khoutu',
  'settings.security.twoFactor.title': 'Netefatso ea mekhahlelo e \'meli',
  'settings.team.actions.invite': 'Memela setho sa sehlopha',
  'settings.team.columns.joined': 'O kenetse',
  'settings.team.form.tempPassword': 'Phasewete ea nakoana',
  'settings.team.inviteFailed': 'Ho memela ho hlolehile',
  'settings.team.loadError': 'Ha ea khona ho kenya sehlopha.',
  'settings.webhooks.actions.add': 'Eketsa webhook',
  'settings.webhooks.columns.events': 'Liketsahalo',
  'settings.webhooks.empty': 'Ha ho lingoliso tsa li-webhook hajoale.',
  'settings.webhooks.form.events': 'Mefuta ea liketsahalo',
  'settings.webhooks.form.url': 'URL ea sebaka',
  'settings.webhooks.loadError': 'Ha ea khona ho kenya li-webhook.',
  'settings.webhooks.modal.delete.title': 'Hlakola webhook?',
  'settings.webhooks.title': 'Lingoliso tsa li-webhook',
  'settings.webhooks.toast.deleted': 'Webhook e hlakotsoe.',
  'settings.webhooks.deliveries.response': 'Seo moamoheli a se romelletseng morao',
  'settings.webhooks.deliveries.showing': 'E bontsha {{shown}} ho {{total}}',
  'settings.webhooks.result.success': 'E fihlisitswe',
  'settings.webhooks.result.blacklisted': 'E hanoe ke moamoheli',
  'settings.notifications.channel.email': 'Imeile',
  'settings.notifications.channel.whatsapp': 'WhatsApp',
  'settings.notifications.channel.webhook': 'Webhook',
  'settings.notifications.channel.inApp': 'Ka hare ho app',
  'settings.notifications.toggle.depleted.label': 'Chelete e felile',
  'settings.notifications.toggle.recovered.label': 'Chelete e khutlile',
  'settings.notifications.toggle.dailySpend.label': 'Tshebeliso ya letsatsi le letsatsi',
  'settings.notifications.toggle.autoTopupSucceeded.label': 'Ho tlatsa ka boiketsiso ho atlehile',
  'settings.notifications.toggle.autoTopupFailed.label': 'Ho tlatsa ka boiketsiso ho hlolehile',
  'settings.apiKeys.seeDocs': 'Bala ditokomane tse feletseng tsa API',
  'settings.nav.contactFields': 'Dikarolo tsa bokopano',
  'settings.hub.contactFields.hint': 'Dintlha tse eketsehileng tseo o di bolokang ka motho ka mong eo o buisanang le yena.',
  'settings.assistant.smallTalk.title': 'Moqoqo o monyane',
  'settings.assistant.smallTalk.intro': 'Dikarabo melaetsa e leng ditebogo feela, tumellano kapa kgaiso, jwalo ka "ke a leboha", "k" kapa "sala hantle". Di romelwa hang, ha di lefellwe mme ha di ise di ye tseleng ya ho se tiisehe. Puo ya moqoqo le mantswe a Afrika Borwa (tnx, sharp, dankie, ngiyabonga) a a utlwisiswa.',
  'settings.assistant.smallTalk.enabled': 'Araba moqoqo o monyane',
  'settings.assistant.smallTalk.thanks': 'Ha moreki a leboha',
  'settings.assistant.smallTalk.acknowledge': 'Ha moreki a re ok',
  'settings.assistant.smallTalk.goodbye': 'Ha moreki a laela',
  'settings.assistant.smallTalk.hint': '{{contact.name ?? there}} e fetoha lebitso la moreki. E tlohele e se na letho bakeng sa sa kamehla.',
  'settings.assistant.moderation.title': 'Puo e sa amoheleheng',
  'settings.assistant.moderation.intro': 'Dithohako, ditlhapa le mantswe a kgethollo a tswang ho bareki a fumana karabo e tiileng, mme moqoqo o tshwaelwa ka ntlha bakeng sa sehlopha sa hao. Moemedi a ka thibela moreki ho Ditlhophiso tsa moqoqo.',
  'settings.assistant.moderation.enabled': 'Hlokomela puo e sa amoheleheng',
  'settings.assistant.moderation.action': 'Kamora ho araba',
  'settings.assistant.moderation.actionHint': 'Ho fetisa ho emisa bot, e le hore sehlopha sa hao se tswelle ka moqoqo.',
  'settings.assistant.moderation.actions.warn': 'Tswela pele ho araba',
  'settings.assistant.moderation.actions.handover': 'Fetisetsa moqoqo sehlopheng',
  'settings.assistant.moderation.reply': 'Karabo ho moreki',
  'settings.assistant.moderation.replyHint': 'E tlohele e se na letho bakeng sa sa kamehla.',
  'settings.assistant.moderation.extra': 'Mantswe a ho eketsa',
  'settings.assistant.moderation.extraHint': 'Lentswe le le leng kapa poleloana moleng ka mong. A bapiswa e le mantswe a feletseng, ho kenyeletswa le mopeleto o patilweng jwalo ka f*ck.',
  'settings.assistant.moderation.allowed': 'Mantswe a ho dumella',
  'settings.assistant.moderation.allowedHint': 'Mantswe a lenaneng la kamehla ao kgwebo ya hao e sa tshwenyehang ka ona, le le leng moleng ka mong.',
  'settings.assistant.moderation.showDefault': 'Sheba lenane la kamehla (mantswe a {{count}})',
  'settings.assistant.moderation.hideDefault': 'Pata lenane la kamehla',
  'settings.assistant.pauseNotice.title': 'Ha sehlopha sa hao se na le moqoqo',
  'settings.assistant.pauseNotice.intro': 'Moreki ya ngolang ha motho a na le moqoqo o bolellwa hang ka letsatsi, ka dikonopo tsa ho emela sehlopha kapa ho botsa mothusi. Ha ho letho le romelwang haeba sehlopha sa hao se arabile metsotsong e 10 e fetileng.',
  'settings.assistant.pauseNotice.enabled': 'Bolella moreki',
  'settings.assistant.pauseNotice.message': 'Molaetsa',
  'settings.assistant.pauseNotice.hint': 'E tlohele e se na letho bakeng sa sa kamehla.'
};

export default settings;
